import React, { useState, useRef, useEffect, useCallback } from 'react';

interface Sentence {
  Text: string;
  audiourl: string;
  translations?: string[];
  aiexplain?: string;
  soundmark?: string;
}

interface AudioListPlayerProps {
  audioList: Sentence[];
  title: string;
  targetRepeats?: number;
  initialAudioIndex?: number;
  onTargetRepeatsChange?: (repeats: number) => void;
  onAudioIndexChange?: (index: number) => void;
  onNextSubCategory?: () => Promise<boolean>;
  hasNextSubCategory?: boolean;
  onBack: () => void;
}

const AudioListPlayer: React.FC<AudioListPlayerProps> = ({ 
  audioList, 
  title, 
  targetRepeats: propTargetRepeats = 1,
  initialAudioIndex = 0,
  onTargetRepeatsChange,
  onAudioIndexChange,
  onNextSubCategory,
  hasNextSubCategory = false,
  onBack 
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialAudioIndex);
  const [currentRepeats, setCurrentRepeats] = useState(0);
  const [targetRepeats, setTargetRepeats] = useState(propTargetRepeats);
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoPlay, setAutoPlay] = useState(false);
  const [status, setStatus] = useState('Ready');
  const [showAiExplain, setShowAiExplain] = useState(false);
  const [wasPlayingBeforeExplain, setWasPlayingBeforeExplain] = useState(false);
  
  const audioRef = useRef<HTMLAudioElement>(new Audio());

  // Sync with parent's target repeats
  useEffect(() => {
    setTargetRepeats(propTargetRepeats);
  }, [propTargetRepeats]);

  // Sync with initial audio index
  useEffect(() => {
    setCurrentIndex(initialAudioIndex);
  }, [initialAudioIndex]);

  // Notify parent when audio index changes
  useEffect(() => {
    onAudioIndexChange?.(currentIndex);
  }, [currentIndex, onAudioIndexChange]);

  const handleTargetRepeatsChange = (newRepeats: number) => {
    setTargetRepeats(newRepeats);
    onTargetRepeatsChange?.(newRepeats);
  };

  const playCurrentAudio = useCallback(() => {
    if (audioList.length === 0 || currentIndex >= audioList.length) return;
    
    const sentence = audioList[currentIndex];
    const audio = audioRef.current;
    audio.src = sentence.audiourl;
    audio.play();
    const newRepeats = currentRepeats + 1;
    setCurrentRepeats(newRepeats);
    setStatus(`Playing ${currentIndex + 1}/${audioList.length} (${newRepeats}/${targetRepeats})`);
  }, [audioList, currentIndex, currentRepeats, targetRepeats]);

  const moveToNext = useCallback(async () => {
    if (currentIndex < audioList.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setCurrentRepeats(0);
    } else if (hasNextSubCategory && onNextSubCategory) {
      // 播放完当前列表，自动进入下一个二级分类
      const success = await onNextSubCategory();
      if (success) {
        setCurrentIndex(0);
        setCurrentRepeats(0);
        setStatus('Loading next subcategory...');
      } else {
        setCurrentIndex(0); // 循环到第一个
        setCurrentRepeats(0);
      }
    } else {
      setCurrentIndex(0); // 循环到第一个
      setCurrentRepeats(0);
    }
  }, [currentIndex, audioList.length, hasNextSubCategory, onNextSubCategory]);

  const moveToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    } else {
      setCurrentIndex(audioList.length - 1);
    }
    setCurrentRepeats(0);
  }, [currentIndex, audioList.length]);

  // 音频结束处理
  useEffect(() => {
    const audio = audioRef.current;
    
    const handleAudioEnd = () => {
      if (currentRepeats < targetRepeats && isPlaying) {
        setTimeout(() => playCurrentAudio(), 500);
      } else if (isPlaying && autoPlay) {
        setTimeout(() => moveToNext(), 1000);
      }
    };

    const handleAudioError = () => {
      if (isPlaying && autoPlay) {
        setTimeout(() => moveToNext(), 1000);
      }
    };

    audio.addEventListener('ended', handleAudioEnd);
    audio.addEventListener('error', handleAudioError);
    
    return () => {
      audio.removeEventListener('ended', handleAudioEnd);
      audio.removeEventListener('error', handleAudioError);
    };
  }, [currentRepeats, targetRepeats, isPlaying, autoPlay, playCurrentAudio, moveToNext]);

  // 当索引改变时自动播放（仅在自动模式下）
  useEffect(() => {
    if (isPlaying && autoPlay && audioList.length > 0 && currentRepeats === 0) {
      const timer = setTimeout(() => {
        playCurrentAudio();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, audioList, isPlaying, autoPlay, currentRepeats, playCurrentAudio]);

  // Reset when audioList changes (new subcategory loaded)
  useEffect(() => {
    setCurrentIndex(0);
    setCurrentRepeats(0);
    setShowAiExplain(false);
  }, [audioList]);

  const play = () => {
    if (audioList.length === 0) return;
    setCurrentRepeats(0);
    setIsPlaying(true);
    setAutoPlay(false);
    playCurrentAudio();
  };

  const pause = () => {
    const audio = audioRef.current;
    audio.pause();
    setIsPlaying(false);
    setAutoPlay(false);
    setStatus('Paused');
  };

  const resume = () => {
    if (audioList.length === 0) return;
    setIsPlaying(true);
    setAutoPlay(true);
    if (currentRepeats === 0) {
      playCurrentAudio();
    } else {
      const audio = audioRef.current;
      audio.play();
    }
  };

  const startAutoPlay = () => {
    setIsPlaying(true);
    setAutoPlay(true);
    setCurrentRepeats(0);
    
    if (audioList.length > 0) {
      setTimeout(() => playCurrentAudio(), 500);
    }
  };

  const stop = () => {
    const audio = audioRef.current;
    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
    setAutoPlay(false);
    setStatus('Stopped');
    setShowAiExplain(false);
    setWasPlayingBeforeExplain(false);
  };

  const previousAudio = () => {
    stop();
    moveToPrevious();
  };

  const nextAudio = () => {
    stop();
    moveToNext();
  };

  const toggleAiExplain = () => {
    if (!showAiExplain) {
      setWasPlayingBeforeExplain(isPlaying && autoPlay);
      if (isPlaying) {
        pause();
      }
    } else {
      if (wasPlayingBeforeExplain) {
        resume();
      }
    }
    setShowAiExplain(!showAiExplain);
  };

  const currentSentence = audioList[currentIndex];

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg p-6 shadow-lg">
          <div className="flex items-center mb-6">
            <button
              onClick={onBack}
              className="mr-4 p-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
            >
              ← Back
            </button>
            <h1 className="text-lg font-bold">{title}</h1>
            {hasNextSubCategory && (
              <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">
                Auto-Next
              </span>
            )}
          </div>
          
          <div className="text-lg text-center mb-6 border rounded-lg p-4 bg-gray-50">
            <div className="font-medium text-gray-800 mb-2">
              {currentSentence?.Text || 'No audio available'}
            </div>
            {currentSentence?.soundmark && (
              <div className="text-sm text-blue-600 mb-2 font-mono">
                [{currentSentence.soundmark}]
              </div>
            )}
            {currentSentence?.translations && (
              <div className="text-sm text-gray-600 space-y-1">
                {currentSentence.translations.map((translation, index) => (
                  <div key={index}>{translation}</div>
                ))}
              </div>
            )}
            {currentSentence?.aiexplain && (
              <div className="mt-3">
                <button
                  onClick={toggleAiExplain}
                  className="px-3 py-1 bg-purple-500 text-white text-sm rounded hover:bg-purple-600 transition-colors"
                >
                  {showAiExplain ? '收起解释' : '🤖 AI解释'}
                </button>
                {showAiExplain && (
                  <div className="mt-2 p-3 bg-purple-50 border border-purple-200 rounded text-sm text-gray-700 text-left">
                    {currentSentence.aiexplain}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="text-center mb-4">
            <label className="mr-2">Repeat:</label>
            <input
              type="number"
              value={targetRepeats}
              onChange={(e) => handleTargetRepeatsChange(parseInt(e.target.value))}
              min="1"
              max="10"
              className="w-16 px-2 py-1 text-center border border-gray-300 rounded"
            />
            <span className="ml-2">times</span>
          </div>
          
          <div className="flex justify-center gap-2 mb-4">
            <button
              onClick={play}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
            >
              Play
            </button>
            <button
              onClick={isPlaying ? pause : resume}
              className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors"
            >
              {isPlaying ? 'Pause' : 'Resume'}
            </button>
            <button
              onClick={startAutoPlay}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Auto
            </button>
            <button
              onClick={stop}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
            >
              Stop
            </button>
          </div>
          
          <div className="flex justify-between mb-4">
            <button
              onClick={previousAudio}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              ← Previous
            </button>
            <button
              onClick={nextAudio}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Next →
            </button>
          </div>
          
          <div className="text-center text-sm text-gray-600 space-y-1">
            <div>{status}</div>
            <div className="text-xs">
              Audio: {currentIndex + 1}/{audioList.length} | 
              {isPlaying ? ' 🔊 Playing' : ' ⏸️ Stopped'} | 
              {autoPlay ? ' 🔄 Auto Mode' : ' 👆 Manual Mode'}
              {hasNextSubCategory && ' | 🔗 Auto-Next Enabled'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioListPlayer;
